/**
 * Lemon Squeezy Subscription Hook
 *
 * Handles Lemon Squeezy checkout, subscription management, and invoices
 * Uses redirect-based checkout (not overlay like Paddle)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_CONFIG;

// Lemon Squeezy Price structure from API
export interface LemonSqueezyPrice {
  id: string;
  productId: string;
  tier: 'free' | 'basic' | 'pro';
  name: string;
  description?: string;
  amount: number;
  currency: string;
  interval: 'day' | 'week' | 'month' | 'year';
  intervalCount: number;
}

// User subscription status
export interface SubscriptionStatus {
  tier: 'free' | 'basic' | 'pro';
  platform: string;
  status: 'active' | 'canceled' | 'expired' | 'paused';
  nextBillingDate?: string;
  canceledAt?: string;
  autoRenewing: boolean;
}

// Invoice from Lemon Squeezy
export interface Invoice {
  id: string;
  status: string;
  billingReason?: string;
  total: number;
  subtotal: number;
  tax: number;
  currency: string;
  createdAt: string;
  updatedAt?: string;
  invoiceUrl?: string;
}

// Hook return type
interface UseLemonSqueezySubscription {
  // Prices
  prices: LemonSqueezyPrice[];
  loadingPrices: boolean;
  pricesError: string | null;

  // Current subscription
  subscription: SubscriptionStatus | null;
  loadingSubscription: boolean;

  // Invoices
  invoices: Invoice[];
  loadingInvoices: boolean;

  // Actions
  createCheckout: (variantId: string) => Promise<{ type: string; checkoutUrl?: string; message?: string }>;
  openCheckout: (variantId: string) => Promise<boolean>;
  cancelSubscription: () => Promise<{ success: boolean; message: string; accessUntil?: string }>;
  resumeSubscription: () => Promise<{ success: boolean; message: string }>;

  // Portal
  getPortal: () => Promise<{ customerPortalUrl?: string; updatePaymentUrl?: string }>;

  // Error handling
  error: string | null;
  clearError: () => void;
}

export default function useLemonSqueezySubscription(): UseLemonSqueezySubscription {
  const [prices, setPrices] = useState<LemonSqueezyPrice[]>([]);
  const [loadingPrices, setLoadingPrices] = useState(true);

  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [pricesError, setPricesError] = useState<string | null>(null);

  // Get auth token - use 'jwtToken' key (consistent with storage.ts)
  const getToken = () => localStorage.getItem('jwtToken');

  const clearError = useCallback(() => setError(null), []);

  // Fetch prices (public endpoint) - use ref to prevent double-fetch in React Strict Mode
  const pricesFetchedRef = useRef(false);

  useEffect(() => {
    if (pricesFetchedRef.current) return;
    pricesFetchedRef.current = true;

    const fetchPrices = async () => {
      console.log('[Lemon Squeezy Hook] Fetching prices from:', `${baseUrl}/subscription/lemonsqueezy/prices`);
      try {
        const response = await axios.get(`${baseUrl}/subscription/lemonsqueezy/prices`);
        console.log('[Lemon Squeezy Hook] Prices response:', response.data);

        if (response.data?.success && Array.isArray(response.data.data)) {
          console.log('[Lemon Squeezy Hook] Setting prices:', response.data.data.length, 'items');
          setPrices(response.data.data);
          setPricesError(null);
        } else {
          console.error('[Lemon Squeezy Hook] Invalid response format');
          setPricesError('Invalid prices response from server');
        }
      } catch (err: any) {
        console.error('[Lemon Squeezy Hook] Failed to fetch prices:', err);
        setPricesError('Failed to load prices');
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchPrices();
  }, []);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      const token = getToken();
      if (!token) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.data) {
          setSubscription(response.data.data);
        }
      } catch (err: any) {
        console.error('Failed to fetch subscription status:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, []);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    setLoadingInvoices(true);
    try {
      const response = await axios.get(`${baseUrl}/subscription/lemonsqueezy/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.success) {
        setInvoices(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  // Load invoices on mount
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Create checkout session
  const createCheckout = useCallback(async (variantId: string) => {
    const token = getToken();
    if (!token) {
      setError('Please log in to subscribe');
      return { type: 'error', message: 'Please log in to subscribe' };
    }

    try {
      // Build success/cancel URLs based on current origin (works for localhost and production)
      const origin = window.location.origin;
      const successUrl = `${origin}/subscription/success`;
      const cancelUrl = `${origin}/subscription/cancel`;

      const response = await axios.post(
        `${baseUrl}/subscription/lemonsqueezy/create-checkout`,
        { variantId, successUrl, cancelUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data;
      }

      setError('Failed to create checkout');
      return { type: 'error', message: 'Failed to create checkout' };
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Failed to create checkout';
      setError(message);
      return { type: 'error', message };
    }
  }, []);

  // Open checkout (redirect to Lemon Squeezy checkout page)
  const openCheckout = useCallback(async (variantId: string): Promise<boolean> => {
    const result = await createCheckout(variantId);

    if (result.type === 'checkout' && result.checkoutUrl) {
      // Redirect to Lemon Squeezy checkout page
      window.location.href = result.checkoutUrl;
      return true;
    } else if (result.type === 'portal') {
      // User already has subscription
      setError(result.message || 'You already have an active subscription. Please manage it from your subscription settings.');
      return false;
    }

    return false;
  }, [createCheckout]);

  // Cancel subscription
  const cancelSubscription = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('Please log in');
      return { success: false, message: 'Please log in' };
    }

    try {
      const response = await axios.post(
        `${baseUrl}/subscription/lemonsqueezy/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        // Refresh subscription status
        const statusRes = await axios.get(`${baseUrl}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (statusRes.data?.data) {
          setSubscription(statusRes.data.data);
        }
        return response.data.data;
      }

      setError('Failed to cancel subscription');
      return { success: false, message: 'Failed to cancel subscription' };
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Failed to cancel subscription';
      setError(message);
      return { success: false, message };
    }
  }, []);

  // Resume subscription
  const resumeSubscription = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('Please log in');
      return { success: false, message: 'Please log in' };
    }

    try {
      const response = await axios.post(
        `${baseUrl}/subscription/lemonsqueezy/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        // Refresh subscription status
        const statusRes = await axios.get(`${baseUrl}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (statusRes.data?.data) {
          setSubscription(statusRes.data.data);
        }
        return response.data.data;
      }

      setError('Failed to resume subscription');
      return { success: false, message: 'Failed to resume subscription' };
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Failed to resume subscription';
      setError(message);
      return { success: false, message };
    }
  }, []);

  // Get portal URLs
  const getPortal = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('Please log in');
      return {};
    }

    try {
      const response = await axios.post(
        `${baseUrl}/subscription/lemonsqueezy/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data;
      }

      return {};
    } catch (err: any) {
      console.error('Failed to get portal:', err);
      return {};
    }
  }, []);

  return {
    prices,
    loadingPrices,
    pricesError,
    subscription,
    loadingSubscription,
    invoices,
    loadingInvoices,
    createCheckout,
    openCheckout,
    cancelSubscription,
    resumeSubscription,
    getPortal,
    error,
    clearError,
  };
}
